import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import {
  ContactAutocompleteService,
  ContactAutocompleteResult,
  ContactDetails
} from '../services/contacts/contact-autocomplete.service';
import { FreightmancersRateFriendComponent } from '../rates/freightmancers-rate-friend/freightmancers-rate-friend.component';


@Component({
  selector: 'app-freightmancer',
  templateUrl: './freightmancer.component.html',
  styleUrls: ['./freightmancer.component.scss']
})
export class FreightmancerComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;

  customerForm!: FormGroup;
  shipperForm!: FormGroup;
  consigneeForm!: FormGroup;
  loadDetailsForm!: FormGroup;
  ratesForm!: FormGroup;

  customerAddresses: { value: string; label: string }[] = [];
  shipperAddresses: { value: string; label: string }[] = [];
  consigneeAddresses: { value: string; label: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private contactSvc: ContactAutocompleteService
  ) {}

  ngOnInit() {
    this.buildForms();
  }

  private buildForms() {
    this.customerForm = this.fb.group({
      customer_uuid: ['', Validators.required],
      customer_address: ['', Validators.required],
      customer_postal_code: [''],
      customer_shipping_bol: [false],
      customer_invoice_bol: [false],
      customer_invoice_pod_required: [false],
      customer_invoice_po_required: [false],
      customer_invoice_po: [''],
      customer_rate_quote: [false],
      customer_consignee_agreement: [false],
      customer_at_pickup_notification: [false],
      customer_delayed_pickup_notification: [false],
      customer_at_delivery_notification: [false],
      customer_delayed_delivery_notification: [false],
      customer_assessorials_incurred_notification: [false],
      customer_csa: [false],
      customer_fast: [false],
      customer_ace: [false],
      customer_aci: [false],
      customer_ct_pat: [false],
      customer_canada_bond: [false],
      customer_us_bond: [false],
      customer_hazmat: [false],
      customer_protect_from_freezing: [false],
      customer_heated: [false],
      customer_refrigeration: [false],
      customer_food_grade: [false],
      rate_method: ['1']
    });

    this.shipperForm = this.fb.group({
      shipper_uuid: ['', Validators.required],
      shipper_address: ['', Validators.required],
      shipper_postal_code: [''],
      shipper_loading_dock: [false],
      shipper_pickup_tailgate: [false],
      shipper_truck_space: [false],
      shipper_pickup_straight_truck: [false],
      shipper_bol_required: [false],
      shipper_pickup_reference: [''],
      estimated_date_shipper: [''],
      estimated_time_start_shipper: [''],
      shipper_is_residential: [false],
      shipper_is_commercial: [false],
      shipper_is_job_site: [false],
      shipper_lift_lull: [false],
      shipper_call_site: [false],
      shipper_unmanned_job_site: [false],
      shipper_job_site_contact_name: [''],
      shipper_job_site_contact_phone: [''],
      shipper_is_convention_center: [false],
      shipper_is_pop_up: [false],
      appointment_shipper: [false],
      appointment_date_shipper: [''],
      appointment_time_start_shipper: [''],
      appointment_time_end_shipper: ['']
    });

    this.consigneeForm = this.fb.group({
      consignee_uuid: ['', Validators.required],
      consignee_address: ['', Validators.required],
      consignee_postal_code: [''],
      consignee_loading_dock: [false],
      consignee_pickup_tailgate: [false],
      consignee_truck_space: [false],
      consignee_pickup_straight_truck: [false],
      consignee_bol_required: [false],
      consignee_delivery_reference: [''],
      estimated_date_consignee: [''],
      estimated_time_end_consignee: [''],
      consignee_is_residential: [false],
      consignee_is_commercial: [false],
      consignee_is_job_site: [false],
      consignee_lift_lull: [false],
      consignee_call_site: [false],
      consignee_unmanned_job_site: [false],
      consignee_job_site_contact_name: [''],
      consignee_job_site_contact_phone: [''],
      consignee_is_convention_center: [false],
      consignee_is_pop_up: [false],
      appointment_consignee: [false],
      appointment_date_consignee: [''],
      appointment_time_start_consignee: [''],
      appointment_time_end_consignee: ['']
    });

    this.loadDetailsForm = this.fb.group({
      order_scope: [''],
      order_scope_secondary: [''],
      lading_type: this.fb.group({
        usa_domestic: [false],
        canada_domestic: [false],
        usa_crossborder: [false],
        canada_crossborder: [false],
        air_freight: [false],
        client_generated: [false],
        local_delivery: [false],
        ocean_freight: [false]
      }),
      custom_broker_name: [''],
      packaging_type: this.fb.group({
        palleted: [false],
        carton: [false],
        crate: [false],
        loose_floor: [false]
      }),
      operations: this.fb.group({
        carrier_rated: [false],
        carrier_rerated: [false],
        straps_bars: [false],
        protective_equipment: [false],
        operations_notation: ['']
      }),
      equipment: this.fb.group({
        type_of_equipment: [''],
        equipment_options: [''],
        partial_or_full: ['Partial']
      }),
      household_import: this.fb.group({
        importing_passport: [false],
        importing_work_permit_visa: [false],
        importing_inventory_listing: [false],
        importing_new_canadian_address: [false],
        importing_scale_tickets: [false]
      }),
      household_export: this.fb.group({
        exporting_passport: [false],
        exporting_form_3299: [false],
        exporting_power_of_attorney: [false],
        exporting_supplemental_declaration: [false],
        exporting_inventory_listing: [false],
        exporting_i94: [false],
        exporting_scale_tickets: [false]
      }),
      pallets_products: this.fb.array([]),
      additional_info: this.fb.group({
        product_status: ['1'],
        booking_number: [''],
        release_number: [''],
        lading_number: [''],
        trip_number: [''],
        shipment_number: [''],
        requirements: [''],
        load_details: [''],
        load_special_info: [''],
        load_length: [''],
        load_value_unit: ['USD'],
        load_value: [''],
        lading_status: ['']
      })
    });

    this.ratesForm = this.fb.group({
      carrier_company_name: [''],
      shipper_radius: ['5'],
      consignee_radius: ['5']
    });
  }

  get pallets_products(): FormArray {
    return this.loadDetailsForm.get('pallets_products') as FormArray;
  }

  addPalletProductSet() {
    const fg = this.fb.group({
      mode: ['pallet'],
      pallet_num: [''],
      pallet_length: [''],
      pallet_width: [''],
      pallet_height: [''],
      pallet_weight: [''],
      product_model_number: [''],
      product_quantity: [''],
      product_length: [''],
      product_width: [''],
      product_height: [''],
      product_weight: [''],
      product_description: ['']
    });
    this.pallets_products.push(fg);
  }

  removePalletProductSet(i: number) {
    this.pallets_products.removeAt(i);
  }

  onCustomerSelected(opt: ContactAutocompleteResult) {
    this.customerForm.get('customer_uuid')!.setValue(opt.value);
    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.customerAddresses = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [
          addr.address,
          addr.address_extended,
          addr.locality,
          addr.region
        ].filter(Boolean).join(', ')
      }));
      if (this.customerAddresses.length) {
        this.customerForm.get('customer_address')!.setValue(this.customerAddresses[0].value);
      }
      this.customerForm.get('customer_postal_code')!.setValue(data.addresses[0]?.postal_code || '');
    });
  }

  onShipperSelected(opt: ContactAutocompleteResult) {
    this.shipperForm.get('shipper_uuid')!.setValue(opt.value);
    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.shipperAddresses = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [
          addr.address,
          addr.address_extended,
          addr.locality,
          addr.region
        ].filter(Boolean).join(', ')
      }));
      if (this.shipperAddresses.length) {
        this.shipperForm.get('shipper_address')!.setValue(this.shipperAddresses[0].value);
      }
      this.shipperForm.get('shipper_postal_code')!.setValue(data.addresses[0]?.postal_code || '');
    });
  }

  onConsigneeSelected(opt: ContactAutocompleteResult) {
    this.consigneeForm.get('consignee_uuid')!.setValue(opt.value);
    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.consigneeAddresses = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [
          addr.address,
          addr.address_extended,
          addr.locality,
          addr.region
        ].filter(Boolean).join(', ')
      }));
      if (this.consigneeAddresses.length) {
        this.consigneeForm.get('consignee_address')!.setValue(
          this.consigneeAddresses[0].value
        );
      }
      this.consigneeForm.get('consignee_postal_code')!.setValue(data.addresses[0]?.postal_code || '');
    });
  }

  submit() {
    const payload = {
      ...this.customerForm.value,
      ...this.shipperForm.value,
      ...this.consigneeForm.value,
      ...this.loadDetailsForm.value,
      ...this.ratesForm.value
    };
    console.log('Freightmancer payload:', payload);
  }
}